/**
 * The two ways to start an estimate (used on Home and the New estimate page)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, ButtonBase, Grid, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ListAltRoundedIcon from '@mui/icons-material/ListAltRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { IconTile } from '../ui';
import { getEstimateCopy } from './copy';
import { useLanguage } from '../../utils/LanguageContext';

const ModeCard = ({ icon, color, title, description, time, bestFor, bestForLabel, onClick, primary }) => (
  <ButtonBase
    onClick={onClick}
    sx={(theme) => ({
      width: '100%',
      height: '100%',
      display: 'block',
      textAlign: 'left',
      p: { xs: 2, sm: 2.5 },
      borderRadius: '16px',
      border: '1px solid',
      borderColor: primary ? alpha(theme.palette.primary.main, 0.45) : 'divider',
      bgcolor: primary ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.04) : 'background.paper',
      transition: 'border-color 120ms, box-shadow 120ms',
      '&:hover': { borderColor: 'primary.main', boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.12)}` },
      '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    })}
  >
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <IconTile icon={icon} color={color} size={48} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="h6">{title}</Typography>
          <ArrowForwardRoundedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        </Stack>
        <Typography variant="caption" color="primary" fontWeight={600}>
          {time}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {description}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.25 }}>
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {bestForLabel}:
          </Box>{' '}
          {bestFor}
        </Typography>
      </Box>
    </Stack>
  </ButtonBase>
);

const EstimateModeCards = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = getEstimateCopy(language);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <ModeCard
          primary
          icon={<BoltRoundedIcon />}
          color="primary"
          title={t.quickTitle}
          time={t.quickTime}
          description={t.quickDesc}
          bestForLabel={t.bestFor}
          bestFor={t.quickBestFor}
          onClick={() => navigate('/app/estimate/quick')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ModeCard
          icon={<ListAltRoundedIcon />}
          color="secondary"
          title={t.detailedTitle}
          time={t.detailedTime}
          description={t.detailedDesc}
          bestForLabel={t.bestFor}
          bestFor={t.detailedBestFor}
          onClick={() => navigate('/app/estimate/detailed')}
        />
      </Grid>
    </Grid>
  );
};

export default EstimateModeCards;
